import { defineComponent as _defineComponent } from 'vue'
import { resolveComponent as _resolveComponent, createVNode as _createVNode, createElementVNode as _createElementVNode, vShow as _vShow, withDirectives as _withDirectives, Transition as _Transition, withCtx as _withCtx, toDisplayString as _toDisplayString, openBlock as _openBlock, createBlock as _createBlock, createCommentVNode as _createCommentVNode, vModelText as _vModelText, withModifiers as _withModifiers, withKeys as _withKeys, createElementBlock as _createElementBlock, createTextVNode as _createTextVNode, normalizeStyle as _normalizeStyle, unref as _unref, normalizeClass as _normalizeClass } from "vue"

const _hoisted_1 = { id: "main-content" }
const _hoisted_2 = {
  class: "modal",
  id: "modal-loading"
}
const _hoisted_3 = { id: "top-content" }
const _hoisted_4 = { id: "center-content" }
const _hoisted_5 = {
  id: "coordinates",
  class: "info-box"
}
const _hoisted_6 = { class: "coordinates-content" }
const _hoisted_7 = { class: "coordinate-item" }
const _hoisted_8 = { class: "coordinate-item" }
const _hoisted_9 = { id: "right-buttons" }
const _hoisted_10 = { id: "options" }
const _hoisted_11 = {
  key: 1,
  id: "options-content"
}
const _hoisted_12 = { id: "options-top-row" }
const _hoisted_13 = { class: "centered-content pt-4 pb-3 pl-1" }
const _hoisted_14 = {
  id: "crosshairs-row",
  class: "centered-content"
}
const _hoisted_15 = ["disabled"]
const _hoisted_16 = {
  id: "fill-row",
  class: "centered-content"
}
const _hoisted_17 = { id: "bottom-content" }
const _hoisted_18 = { id: "position-form" }
const _hoisted_19 = {
  key: 0,
  id: "body-logos"
}
const _hoisted_20 = { class: "intro-card info-text" }
const _hoisted_21 = { class: "ml-4" }
const _hoisted_22 = { class: "video-wrapper" }
const _hoisted_23 = { class: "text-list mx-5" }

import { ref, reactive, computed, watch, onBeforeMount, onMounted, nextTick, type Ref } from "vue";
import { fmtDegLat, fmtHours, D2R, R2D } from "@wwtelescope/astro";
import { Coordinates, Settings, WWTControl } from "@wwtelescope/engine";
import { GotoRADecZoomParams, engineStore } from "@wwtelescope/engine-pinia";
import { BackgroundImageset, supportsTouchscreen, blurActiveElement, useWWTKeyboardControls } from "@cosmicds/vue-toolkit";
import { useDisplay } from "vuetify";
import { storeToRefs } from "pinia";

import * as wwtlib from "@wwtelescope/engine";

import { drawFootprint } from "./footprint";
import { renderOneFrame, splitString } from "./wwt-hacks";

import { ResolvedObject } from "./simbad_resolvers";

// eslint-disable-next-line @typescript-eslint/ban-ts-comment
// @ts-expect-error `Util.splitString` is defined
type SheetType = "text" | "video";
type CameraParams = Omit<GotoRADecZoomParams, "instant">;
export interface RomanFovProps {
  wwtNamespace?: string;
  initialCameraParams?: CameraParams;
}

const AUTO_SHOW_INFO_KEY = "roman-view-finder__auto-show-info";
const infoFraction = 34;
const DOUBLE_CLICK_INTERVAL_MS = 200;

export default /*@__PURE__*/_defineComponent({
  __name: 'RomanFov',
  props: {
    wwtNamespace: { default: "roman-fov" },
    initialCameraParams: { default: () => {
    return {
      raRad: 1.4612,
      decRad: -0.09646,
      zoomDeg: 60
    };
  } }
  },
  setup(__props: any) {

wwtlib.Util.splitString = splitString;

const store = engineStore();

const {
  backgroundImageset,
  decRad,
  raRad,
} = storeToRefs(store);

const backgroundImagesetName = computed({
  get(): string | undefined {
    return backgroundImageset.value?.get_name();
  },
  set(name: string) {
    store.setBackgroundImageByName(name);
  }
});


useWWTKeyboardControls(store);

const touchscreen = supportsTouchscreen();
const { smAndDown } = useDisplay();

const props = __props;

const backgroundImagesets = reactive<BackgroundImageset[]>([
  new BackgroundImageset("DSS", "Digitized Sky Survey (Color)"),
  new BackgroundImageset("2MASS", "2Mass: Imagery (Infrared)"),
]);
const sheet = ref<SheetType | null>(null);
const layersLoaded = ref(false);
const positionSet = ref(false);

const positionSearchRA = ref<string | null>(null);
const positionSearchDec = ref<string | null>(null);
const positionSearchError = ref<string | null>(null);


// Color palette generated by Claude from https://assets.science.nasa.gov/dynamicimage/assets/science/missions/rst/spacecraft-illustrations/Roman_Title_1.jpg 
// (with some adjustments by me)
// Deep Space Purple #502752(Background)
// Cosmic Violet #632B7D (Background)
// ISM Indigo #8B5FB6 (Call to Action, with Stardust White text)
// pick one accent
// Nebula Magenta #C77FB3 (Accents)
// Stellar Amber #FFB86C (Accents / Contrast)
// Electric Cyan #00F0FF (Accents)
// Soft Lavender #B8A5D4  (Borders)
// Stardust White #F5F0FF  (Background / text on dark)
// Space Black #0A0515  (text on light)
// https://contrast-grid.eightshapes.com/?version=1.1.0&background-colors=&foreground-colors=%23FFFFFF%2C%20white%0D%0A%23502762%2C%20Deep%20Space%20Purple%0D%0A%23632b7d%2C%20Cosmic%20Violet%0D%0A%238B5FB6%2C%20ISM%20Indigo%0D%0A%23C77FB3%2C%20Nebula%20Magenta%0D%0A%23FFB86C%2C%20Stellar%20Amber%0D%0A%2300F0FF%2C%20Electric%20Cyan%0D%0A%23B8A5D4%2C%20Soft%20Lavender%0D%0A%23F5F0FF%2C%20Stardust%20White%0D%0A%230A0515%2C%20Space%20Black%0D%0A%23000000%2C%20black&es-color-form__tile-size=compact&es-color-form__show-contrast=aaa&es-color-form__show-contrast=aa&es-color-form__show-contrast=aa18&es-color-form__show-contrast=dnp

const backgroundColorDarkest = ref("#502752");
const backgroundColor = ref("#632B7D");
const borderColor = ref("#B8A5D4");
const accentColor = ref("#C77FB3");
const textColor = ref("#F5F0FF");
const tab = ref(0);
const footprintColorString = ref("#00F0FF"); // #ff00b7

const decimalCoordinates = ref(false);
const raDisplay = computed(() => {
  return decimalCoordinates.value ? 
    (raRad.value * R2D).toFixed(6) :
    fmtHours(raRad.value, 'h', 'm', 0, 's');
});
const decDisplay = computed(() => {
  return decimalCoordinates.value ? 
    (decRad.value * R2D).toFixed(6) :
    fmtDegLat(decRad.value);
});
const galactic = ref(false);
const crosshairs = ref(false);
const crosshairsColor = ref("#ffffff");
const fill = ref(false);
const fillOpacity = ref(0.5);
const moving = ref(false);

const snackbar = ref(false);
const snackbarColor = ref<"error" | "success">("success");
const snackbarMessage = ref("");

const showInfoDialog = ref(false);
const autoOpenInfoDialog = ref(true);

const settings = Settings.get_active();

const showWebGL2Dialog = ref(false);

function isWebGL2Enabled() {
  // It doesn't seem like there's a better way to do this than just to try and get a context
  // https://developer.mozilla.org/en-US/docs/Web/API/WebGL_API/By_example/Detect_WebGL
  // NB: The engine specifically wants a webgl2 context
  const canvas = document.createElement("canvas");
  const gl = canvas.getContext("webgl2");
  return gl instanceof WebGL2RenderingContext;
}

onBeforeMount(() => {
  autoOpenInfoDialog.value = window.localStorage.getItem(AUTO_SHOW_INFO_KEY)?.toLowerCase() !== "false";
});

onMounted(() => {

  store.waitForReady().then(async () => {

    if (!isWebGL2Enabled()) {
      showWebGL2Dialog.value = true; 
      layersLoaded.value = true;
      positionSet.value = true;
      // eslint-disable-next-lint @typescript-eslint/ban-ts-comment
      // @ts-expect-error `canvas` is defined
      WWTControl.singleton.canvas.setAttribute("hidden", "true");
      WWTControl.singleton.renderOneFrame = function() {};
      return;
    }

    settings.set_galacticMode(galactic.value);
    settings.set_showCrosshairs(crosshairs.value);
    settings.set_crosshairsColor(crosshairsColor.value);

    const control = WWTControl.singleton;
    control.renderOneFrame();
    control.renderOneFrame = renderOneFrame.bind(control);

    const cameraParams = { ...props.initialCameraParams };
    const query = new URLSearchParams(window.location.search);

    const paramNames: Record<string, keyof CameraParams> = {
      "raDeg": "raRad",
      "decDeg": "decRad",
      "zoomDeg": "zoomDeg",
      "rollDeg": "rollRad",
    };
    for (const queryParam of Object.keys(paramNames)) {
      const cameraParam = paramNames[queryParam];
      const valueString = query.get(queryParam);
      if (valueString == null) {
        continue;
      }
      const value = parseFloat(valueString);
      if (!isNaN(value)) {
        const factor = queryParam === "zoomDeg" ? 1 : D2R;
        cameraParams[cameraParam] = value * factor;
      }
    }

    // control._drawCrosshairs = (_renderContext: RenderContext) => { drawFootprint(WWTControl.singleton); };
    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
    // @ts-ignore
    control.renderFrameCallback = function (wwt: WWTControl) {
      drawFootprint(wwt, {
        color: footprintColorString.value,
        fill: fill.value,
        fillOpacity: fillOpacity.value,
      });
    };
    WWTControl.singleton.renderOneFrame();

    store.gotoRADecZoom({
      ...cameraParams,
      instant: true
    }).then(() => positionSet.value = true);

    await store.loadImageCollection({ url: "bg.wtml", loadChildFolders: false }).then(_folder => {
      backgroundImagesets.push(new BackgroundImageset("unWISE", "unWISE color, from W2 and W1 bands"));
      backgroundImagesets.push(new BackgroundImageset("SDSS", "SDSS9 color"));

      const bgName = query.get("bg") ?? "DSS";
      let backgroundName: string | null = null;
      if (bgName) {
        const bgSet = backgroundImagesets.find(bg => bg.displayName === bgName);
        if (bgSet) {
          backgroundName = bgSet.imagesetName; 
        }
      }
      if (backgroundName) {
        backgroundImagesetName.value = backgroundName;
      }
    });

    const url = new URL(window.location.href);
    url.search = "";
    window.history.replaceState({}, document.title, url.toString());

    // If there are layers to set up, do that here!
    layersLoaded.value = true;

    showInfoDialog.value = autoOpenInfoDialog.value;
  });
});

const ready = computed(() => layersLoaded.value && positionSet.value);

/* `isLoading` is a bit redundant here, but it could potentially have independent logic */
const isLoading = computed(() => !ready.value);

/* Properties related to device/screen characteristics */
const smallSize = computed(() => smAndDown.value);

/** Values related to setting the info sheet size and position */
const tall = computed(() => smAndDown.value);
const widescreenInfoLocation = ref<"right" | "bottom">("right");
const infoSheetLocation = computed(() => tall.value || widescreenInfoLocation.value === "bottom" ? "bottom" : "right");
const infoSheetHeight = computed(() => infoSheetLocation.value === "bottom" ? `${infoFraction}%` : "100%");
const infoSheetWidth = computed(() => infoSheetLocation.value === "bottom" ? "100%" : `${infoFraction}%`);
const infoTextHeight = computed(() => infoSheetLocation.value === "bottom" ? `calc(${infoFraction}vh - 25px)` : "calc(100vh - 25px)");
const infoSheetTransition = computed(() => infoSheetLocation.value === "bottom" ? "dialog-bottom-transition" : "tab-reverse-transition");

/* This lets us inject component data into element CSS */
const cssVars = computed(() => {
  return {
    "--accent-color": accentColor.value,
    "--background-color-darkest": backgroundColorDarkest.value,
    "--background-color": backgroundColor.value,
    "--border-color": borderColor.value,
    "--text-color": textColor.value,
    "--accent-color-2": footprintColorString.value,
    "--app-content-height": showTextSheet.value && infoSheetLocation.value === "bottom" ? `${100 - infoFraction}%` : "100%",
    "--app-content-width": showTextSheet.value && infoSheetLocation.value === "right" ? `${100 - infoFraction}%` : "100%",
    "--info-sheet-width": infoSheetWidth.value,
    "--info-sheet-height": infoSheetHeight.value,
    "--info-text-height": infoTextHeight.value,
  };
});

const showOptions = ref(!smAndDown.value);
/**
  Computed flags that control whether the relevant dialogs display.
  The `sheet` data member stores which sheet is open, so these are just
  computed wrappers around modifying/querying that which can be used as
  dialog v-model values
*/
const showTextSheet = computed({
  get() {
    return sheet.value === "text";
  },
  set(_value: boolean) {
    selectSheet("text");
  }
});

const showVideoSheet = computed({
  get() {
    return sheet.value === "video";
  },
  set(value: boolean) {
    selectSheet("video");
    if (!value) {
      const video = document.querySelector("#info-video") as HTMLVideoElement;
      video.pause();
    }
  }
});

function selectSheet(sheetType: SheetType | null) {
  if (sheet.value === sheetType) {
    sheet.value = null;
    nextTick(() => {
      blurActiveElement();
    });
  } else {
    sheet.value = sheetType;
  }
}

let timeout: ReturnType<typeof setTimeout> | null = null;
let clickCount = 0;
function handlePositionGoToClick(isActive: Ref<boolean>) {
  clickCount += 1;
  if (timeout != null) {
    clearTimeout(timeout);
  }
  if (clickCount > 1) {
    tryGoToSearchPosition(isActive, true);
    clickCount = 0;
    return;
  }
  timeout = setTimeout(() => {
    tryGoToSearchPosition(isActive, false);
    clickCount = 0;
  }, DOUBLE_CLICK_INTERVAL_MS);
}

function parseRA(data: string): number {
  const lower = data.trim().toLowerCase();
  let hours = false;
  if (['h', ':', ' '].some(c => lower.indexOf(c) !== -1)) {
    hours = lower.indexOf('d') === -1; // a d means there was degrees in there
  }
  
  let ra = Coordinates.parse(lower);
  if (hours && ra <= 24) {
    ra *= 15;
  }
  return ra;
}

function tryGoToSearchPosition(menuOpen: Ref<boolean>, instant: boolean = false) {
  positionSearchError.value = null;

  const ra = parseRA(positionSearchRA.value ?? "");
  const dec = Coordinates.parseDec(positionSearchDec.value ?? "");

  const raValid = !isNaN(ra);
  const decValid = !isNaN(dec);

  if (raValid && decValid) {
    store.gotoRADecZoom({
      raRad: ra * D2R,
      decRad: dec * D2R,
      zoomDeg: 20,
      instant,
    });
    menuOpen.value = false;
    return;
  }

  const invalid: string[] = [];
  if (!raValid) {
    invalid.push("right ascension");
  }
  if (!decValid) {
    invalid.push("declination");
  }

  const multiple = invalid.length > 1;
  const isAre = multiple ? "are" : "is";
  positionSearchError.value = `Your value${multiple ? 's' : ''} for ${invalid.join(' and ')} ${isAre} invalid`;
}

function shareURL(): string {
  const url = new URL(window.location.href);
  const bgSet = backgroundImagesets.find(bg => bg.imagesetName === backgroundImagesetName.value);
  let search = `raDeg=${store.raRad*R2D}&decDeg=${store.decRad*R2D}&zoomDeg=${store.zoomDeg}&rollDeg=${store.rollRad*R2D}`;
  if (bgSet) {
    search = `${search}&bg=${bgSet.displayName}`;
  }
  url.search = search;
  return url.href;
}

function copyURLToClipboard() {
  navigator.clipboard
    .writeText(shareURL())
    .then(() => {
      snackbarColor.value = "success";
      snackbarMessage.value = "Shareable URL copied to clipboard";
      snackbar.value = true;
    })
    .catch((_err) => {
      snackbarColor.value = "error";
      snackbarMessage.value = "Failed to copy share URL to clipboard";
      snackbar.value = true;
    });
}

watch(galactic, (gal: boolean) => {
  const raRad = store.raRad;
  const decRad = store.decRad;
  settings.set_galacticMode(gal);
  store.gotoRADecZoom({
    raRad,
    decRad,
    zoomDeg: store.zoomDeg,
    instant: true,
  });
});
watch(crosshairs, (show: boolean) => settings.set_showCrosshairs(show));
watch(crosshairsColor, (color: string) => settings.set_crosshairsColor(color));


function handleResolved(object: ResolvedObject) {
  const {raDeg, decDeg}  = object;
  console.log('Received', object);
  if (raDeg && decDeg) {
    positionSearchRA.value = `${raDeg.toFixed(7)}d`;
    positionSearchDec.value = `${decDeg.toFixed(7)}d`;
  }
  handlePositionGoToClick(ref(true));
}

// const combinedPositionInput = ref('');
// function splitCombinedInput(value: string) {
//   /**
//    * We will accept only simbad compatable formats
//    * The following writings are allowed:
//    * 20 54 05.689 +37 01 17.38
//    * 10:12:45.3-45:17:50
//    * 15h17m-11d10m
//    * 15h17+89d15
//    * 275d11m15.6954s+17d59m59.876s
//    * 12.34567h-17.87654d
//    * 350.123456d-17.33333d <=> 350.123456 -17.33333 
//    */
// }

watch(autoOpenInfoDialog, (open: boolean) => {
  window.localStorage.setItem(AUTO_SHOW_INFO_KEY, open.toString());
});

return (_ctx: any,_cache: any) => {
  const _component_WorldWideTelescope = _resolveComponent("WorldWideTelescope")!
  const _component_icon_button = _resolveComponent("icon-button")!
  const _component_v_select = _resolveComponent("v-select")!
  const _component_v_checkbox = _resolveComponent("v-checkbox")!
  const _component_v_slider = _resolveComponent("v-slider")!
  const _component_v_snackbar = _resolveComponent("v-snackbar")!
  const _component_simbad_resolver = _resolveComponent("simbad-resolver")!
  const _component_v_col = _resolveComponent("v-col")!
  const _component_v_text_field = _resolveComponent("v-text-field")!
  const _component_v_btn = _resolveComponent("v-btn")!
  const _component_v_row = _resolveComponent("v-row")!
  const _component_credit_logos = _resolveComponent("credit-logos")!
  const _component_font_awesome_icon = _resolveComponent("font-awesome-icon")!
  const _component_v_list_item = _resolveComponent("v-list-item")!
  const _component_v_card_actions = _resolveComponent("v-card-actions")!
  const _component_v_dialog = _resolveComponent("v-dialog")!
  const _component_v_card = _resolveComponent("v-card")!
  const _component_v_tab = _resolveComponent("v-tab")!
  const _component_v_tabs = _resolveComponent("v-tabs")!
  const _component_v_chip = _resolveComponent("v-chip")!
  const _component_v_spacer = _resolveComponent("v-spacer")!
  const _component_funding_acknowledgement = _resolveComponent("funding-acknowledgement")!
  const _component_v_container = _resolveComponent("v-container")!
  const _component_v_card_text = _resolveComponent("v-card-text")!
  const _component_v_window_item = _resolveComponent("v-window-item")!
  const _component_v_window = _resolveComponent("v-window")!
  const _component_v_app = _resolveComponent("v-app")!

  return (_openBlock(), _createBlock(_component_v_app, {
    id: "app",
    style: _normalizeStyle(cssVars.value)
  }, {
    default: _withCtx(() => [
      _createElementVNode("div", _hoisted_1, [
        _createVNode(_component_WorldWideTelescope, { "wwt-namespace": __props.wwtNamespace }, null, 8, ["wwt-namespace"]),
        _cache[79] || (_cache[79] = _createElementVNode("canvas", { id: "shadow" }, null, -1)),
        _createVNode(_Transition, { name: "fade" }, {
          default: _withCtx(() => [
            _withDirectives(_createElementVNode("div", _hoisted_2, [...(_cache[38] || (_cache[38] = [
              _createElementVNode("div", { class: "container" }, [
                _createElementVNode("div", { class: "spinner" }),
                _createElementVNode("p", null, "Loading …")
              ], -1)
            ]))], 512), [
              [_vShow, isLoading.value]
            ])
          ]),
          _: 1
        }),
        _createElementVNode("div", _hoisted_3, [
          _cache[40] || (_cache[40] = _createElementVNode("div", { id: "left-buttons" }, null, -1)),
          _createElementVNode("div", _hoisted_4, [
            _createElementVNode("div", _hoisted_5, [
              _createElementVNode("div", _hoisted_6, [
                _createElementVNode("span", _hoisted_7, "RA: " + _toDisplayString(raDisplay.value), 1),
                _createElementVNode("span", _hoisted_8, "Dec: " + _toDisplayString(decDisplay.value), 1)
              ])
            ])
          ]),
          _createElementVNode("div", _hoisted_9, [
            _createElementVNode("div", _hoisted_10, [
              (!showOptions.value)
                ? (_openBlock(), _createBlock(_component_icon_button, {
                    key: 0,
                    id: "options-closed",
                    "fa-icon": "sliders",
                    color: borderColor.value,
                    "tooltip-text": "Open controls",
                    "tooltip-location": "start",
                    onActivate: _cache[0] || (_cache[0] = ($event: any) => (showOptions.value = !showOptions.value)),
                    tabindex: "0",
                    border: false,
                    "background-color": "transparent"
                  }, null, 8, ["color"]))
                : _createCommentVNode("", true),
              (showOptions.value)
                ? (_openBlock(), _createElementBlock("div", _hoisted_11, [
                    _createElementVNode("div", _hoisted_12, [
                      _createVNode(_component_v_select, {
                        id: "bg-select",
                        class: "mt-3 ml-1",
                        width: "165",
                        modelValue: backgroundImagesetName.value,
                        "onUpdate:modelValue": _cache[1] || (_cache[1] = ($event: any) => ((backgroundImagesetName).value = $event)),
                        label: "Select Background",
                        items: backgroundImagesets,
                        "list-props": {bgColor: backgroundColorDarkest.value},
                        variant: "solo-filled",
                        "item-title": "displayName",
                        "item-value": "imagesetName",
                        "bg-color": backgroundColor.value,
                        "item-color": textColor.value,
                        density: "compact",
                        "hide-details": ""
                      }, null, 8, ["modelValue", "items", "list-props", "bg-color", "item-color"]),
                      _createVNode(_component_icon_button, {
                        id: "options-open",
                        class: "pt-0 px-0",
                        "fa-icon": "chevron-up",
                        color: borderColor.value,
                        "tooltip-text": "Close controls",
                        "tooltip-location": "start",
                        onActivate: _cache[2] || (_cache[2] = ($event: any) => (showOptions.value = !showOptions.value)),
                        tabindex: "0",
                        border: false,
                        "background-color": "transparent"
                      }, null, 8, ["color"])
                    ]),
                    _createElementVNode("div", _hoisted_13, [
                      _cache[39] || (_cache[39] = _createElementVNode("label", { for: "footprint-color" }, " Footprint Color: ", -1)),
                      _withDirectives(_createElementVNode("input", {
                        id: "footprint-color",
                        class: "bordered",
                        type: "color",
                        "onUpdate:modelValue": _cache[3] || (_cache[3] = ($event: any) => ((footprintColorString).value = $event))
                      }, null, 512), [
                        [_vModelText, footprintColorString.value]
                      ])
                    ]),
                    _createElementVNode("div", _hoisted_14, [
                      _createVNode(_component_v_checkbox, {
                        modelValue: crosshairs.value,
                        "onUpdate:modelValue": _cache[4] || (_cache[4] = ($event: any) => ((crosshairs).value = $event)),
                        label: "Crosshairs",
                        density: "compact",
                        "hide-details": "",
                        onKeydown: [
                          _cache[5] || (_cache[5] = _withKeys(_withModifiers(($event: any) => (crosshairs.value = !crosshairs.value), ["prevent"]), ["space"])),
                          _cache[6] || (_cache[6] = _withKeys(_withModifiers(($event: any) => (crosshairs.value = !crosshairs.value), ["prevent"]), ["enter"]))
                        ]
                      }, null, 8, ["modelValue"]),
                      _withDirectives(_createElementVNode("input", {
                        id: "crosshairs-color",
                        class: "bordered",
                        type: "color",
                        "onUpdate:modelValue": _cache[7] || (_cache[7] = ($event: any) => ((crosshairsColor).value = $event)),
                        disabled: !crosshairs.value
                      }, null, 8, _hoisted_15), [
                        [_vShow, crosshairs.value],
                        [_vModelText, crosshairsColor.value]
                      ])
                    ]),
                    _createElementVNode("div", _hoisted_16, [
                      _createVNode(_component_v_checkbox, {
                        modelValue: fill.value,
                        "onUpdate:modelValue": _cache[8] || (_cache[8] = ($event: any) => ((fill).value = $event)),
                        label: "Fill",
                        density: "compact",
                        "hide-details": "",
                        onKeydown: [
                          _cache[9] || (_cache[9] = _withKeys(_withModifiers(($event: any) => (fill.value = !fill.value), ["prevent"]), ["space"])),
                          _cache[10] || (_cache[10] = _withKeys(_withModifiers(($event: any) => (fill.value = !fill.value), ["prevent"]), ["enter"]))
                        ]
                      }, null, 8, ["modelValue"]),
                      _createVNode(_component_v_slider, {
                        modelValue: fillOpacity.value,
                        "onUpdate:modelValue": _cache[11] || (_cache[11] = ($event: any) => ((fillOpacity).value = $event)),
                        min: 0,
                        max: 1,
                        step: 0.01,
                        disabled: !fill.value,
                        color: footprintColorString.value,
                        density: "compact",
                        "hide-details": ""
                      }, null, 8, ["modelValue", "disabled", "color"])
                    ]),
                    _createVNode(_component_v_checkbox, {
                      modelValue: decimalCoordinates.value,
                      "onUpdate:modelValue": _cache[12] || (_cache[12] = ($event: any) => ((decimalCoordinates).value = $event)),
                      label: "Decimal coordinates",
                      density: "compact",
                      "hide-details": "",
                      onKeydown: [
                        _cache[13] || (_cache[13] = _withKeys(_withModifiers(($event: any) => (decimalCoordinates.value = !decimalCoordinates.value), ["prevent"]), ["space"])),
                        _cache[14] || (_cache[14] = _withKeys(_withModifiers(($event: any) => (decimalCoordinates.value = !decimalCoordinates.value), ["prevent"]), ["enter"]))
                      ]
                    }, null, 8, ["modelValue"]),
                    _createVNode(_component_v_checkbox, {
                      modelValue: galactic.value,
                      "onUpdate:modelValue": _cache[15] || (_cache[15] = ($event: any) => ((galactic).value = $event)),
                      label: "Galactic mode",
                      density: "compact",
                      "hide-details": "",
                      onKeydown: [
                        _cache[16] || (_cache[16] = _withKeys(_withModifiers(($event: any) => (galactic.value = !galactic.value), ["prevent"]), ["space"])),
                        _cache[17] || (_cache[17] = _withKeys(_withModifiers(($event: any) => (galactic.value = !galactic.value), ["prevent"]), ["enter"]))
                      ]
                    }, null, 8, ["modelValue"])
                  ]))
                : _createCommentVNode("", true)
            ]),
            _createElementVNode("div", null, [
              _createVNode(_component_icon_button, {
                id: "info-icon",
                modelValue: showTextSheet.value,
                "onUpdate:modelValue": _cache[18] || (_cache[18] = ($event: any) => ((showTextSheet).value = $event)),
                "fa-icon": "info",
                color: borderColor.value,
                "tooltip-text": "Show User Guide",
                "tooltip-location": "start"
              }, null, 8, ["modelValue", "color"])
            ]),
            _createElementVNode("div", null, [
              _createVNode(_component_icon_button, {
                id: "share-icon",
                "fa-icon": "fa-share-nodes",
                color: borderColor.value,
                "tooltip-text": "Copy share URL",
                "tooltip-location": "start",
                onActivate: copyURLToClipboard
              }, null, 8, ["color"]),
              _createVNode(_component_v_snackbar, {
                modelValue: snackbar.value,
                "onUpdate:modelValue": _cache[19] || (_cache[19] = ($event: any) => ((snackbar).value = $event)),
                color: snackbarColor.value
              }, {
                default: _withCtx(() => [
                  _createTextVNode(_toDisplayString(snackbarMessage.value), 1)
                ]),
                _: 1
              }, 8, ["modelValue", "color"])
            ])
          ])
        ]),
        _createElementVNode("div", _hoisted_17, [
          _createVNode(_component_v_row, {
            id: "position-layout",
            align: "start",
            justify: "center",
            class: "mb-1"
          }, {
            default: _withCtx(() => [
              _createVNode(_component_v_col, {
                cols: "10",
                md: "auto",
                class: "d-flex align-start ga-3 pl-0",
                style: {"min-width":"250px"}
              }, {
                default: _withCtx(() => [
                  _cache[41] || (_cache[41] = _createElementVNode("div", { class: "position-label" }, "Go to", -1)),
                  _createVNode(_component_simbad_resolver, {
                    onResolved: handleResolved,
                    "button-color": borderColor.value,
                    class: "flex-grow-1"
                  }, null, 8, ["button-color"])
                ]),
                _: 1
              }),
              _createVNode(_component_v_col, {
                cols: "10",
                md: "6",
                class: "d-flex align-start ga-3 pl-0 flex-grow-1"
              }, {
                default: _withCtx(() => [
                  _cache[42] || (_cache[42] = _createElementVNode("div", { class: "position-label" }, "or", -1)),
                  _createElementVNode("div", _hoisted_18, [
                    _createVNode(_component_v_text_field, {
                      onKeydown: _cache[20] || (_cache[20] = _withModifiers(() => {}, ["stop"])),
                      modelValue: positionSearchRA.value,
                      "onUpdate:modelValue": _cache[21] || (_cache[21] = ($event: any) => ((positionSearchRA).value = $event)),
                      label: "RA",
                      density: "compact",
                      "bg-color": "black",
                      variant: "outlined",
                      hint: "HMS or decimal degrees",
                      "persistent-hint": ""
                    }, null, 8, ["modelValue"]),
                    _createVNode(_component_v_text_field, {
                      onKeydown: _cache[22] || (_cache[22] = _withModifiers(() => {}, ["stop"])),
                      modelValue: positionSearchDec.value,
                      "onUpdate:modelValue": _cache[23] || (_cache[23] = ($event: any) => ((positionSearchDec).value = $event)),
                      label: "Dec",
                      density: "compact",
                      "bg-color": "black",
                      variant: "outlined",
                      hint: "DMS or decimal degrees",
                      "persistent-hint": ""
                    }, null, 8, ["modelValue"]),
                    _createVNode(_component_v_btn, {
                      onClick: _cache[24] || (_cache[24] = () => handlePositionGoToClick(ref(true))),
                      loading: moving.value,
                      color: borderColor.value,
                      text: "Go",
                      type: "Submit",
                      density: "comfortable",
                      class: "mt-1"
                    }, null, 8, ["loading", "color"])
                  ])
                ]),
                _: 1
              })
            ]),
            _: 1
          }),
          (positionSearchError.value)
            ? (_openBlock(), _createBlock(_component_v_snackbar, {
                key: 0,
                text: positionSearchError.value,
                type: "error",
                density: "compact",
                class: "pt-2",
                location: "top"
              }, null, 8, ["text"]))
            : _createCommentVNode("", true)
        ]),
        (!smallSize.value)
          ? (_openBlock(), _createElementBlock("div", _hoisted_19, [
              _createVNode(_component_credit_logos)
            ]))
          : _createCommentVNode("", true),
        _createVNode(_component_v_dialog, {
          modelValue: showInfoDialog.value,
          "onUpdate:modelValue": _cache[28] || (_cache[28] = ($event: any) => ((showInfoDialog).value = $event)),
          style: _normalizeStyle(cssVars.value),
          persistent: "",
          scrim: false,
          id: "info-dialog",
          "content-class": "info-dialog-content"
        }, {
          default: _withCtx(() => [
            _createVNode(_component_font_awesome_icon, {
              id: "info-close-icon",
              class: "close-icon",
              icon: "times",
              size: "lg",
              onClick: _cache[25] || (_cache[25] = ($event: any) => (showInfoDialog.value = false)),
              onKeyup: _cache[26] || (_cache[26] = _withKeys(($event: any) => (showInfoDialog.value = false), ["enter"])),
              tabindex: "0"
            }),
            _createElementVNode("div", _hoisted_20, [
              _cache[50] || (_cache[50] = _createElementVNode("h3", { class: "mb-3" }, " Quick Start ", -1)),
              _cache[51] || (_cache[51] = _createElementVNode("h4", null, "Navigation", -1)),
              _createElementVNode("ul", _hoisted_21, [
                _createVNode(_component_v_list_item, { density: "compact" }, {
                  default: _withCtx(() => [...(_cache[43] || (_cache[43] = [
                    _createElementVNode("strong", null, "Pan:", -1),
                    _createTextVNode(" Click + drag. ", -1)
                  ]))]),
                  _: 1
                }),
                _createVNode(_component_v_list_item, { density: "compact" }, {
                  default: _withCtx(() => [...(_cache[44] || (_cache[44] = [
                    _createElementVNode("strong", null, "Zoom:", -1),
                    _createTextVNode(" Scroll in and out ", -1)
                  ]))]),
                  _: 1
                }),
                _createVNode(_component_v_list_item, { density: "compact" }, {
                  default: _withCtx(() => [...(_cache[45] || (_cache[45] = [
                    _createElementVNode("strong", null, "Rotate:", -1),
                    _createTextVNode(" Press ", -1),
                    _createElementVNode("strong", null, "ctrl", -1),
                    _createTextVNode(" + click + drag ", -1)
                  ]))]),
                  _: 1
                })
              ]),
              _cache[52] || (_cache[52] = _createElementVNode("h4", { class: "mt-2" }, "Buttons", -1)),
              _createElementVNode("ul", null, [
                _createVNode(_component_v_list_item, { density: "compact" }, {
                  prepend: _withCtx(() => [
                    _createVNode(_component_font_awesome_icon, {
                      icon: "info",
                      size: "lg",
                      class: "bullet-icon"
                    })
                  ]),
                  default: _withCtx(() => [
                    _cache[46] || (_cache[46] = _createTextVNode(" View all documentation ", -1))
                  ]),
                  _: 1
                }),
                _createVNode(_component_v_list_item, { density: "compact" }, {
                  prepend: _withCtx(() => [
                    _createVNode(_component_font_awesome_icon, {
                      icon: "share-nodes",
                      size: "lg",
                      class: "bullet-icon"
                    })
                  ]),
                  default: _withCtx(() => [
                    _cache[47] || (_cache[47] = _createElementVNode("strong", null, "Share", -1)),
                    _cache[48] || (_cache[48] = _createTextVNode(" current view. Url will be copied to clipboard and can be pasted in browser. ", -1))
                  ]),
                  _: 1
                })
              ]),
              _createVNode(_component_v_card_actions, { class: "pb-0" }, {
                default: _withCtx(() => [
                  _createVNode(_component_v_btn, {
                    variant: "text",
                    color: accentColor.value,
                    onClick: _cache[27] || (_cache[27] = () => {
              autoOpenInfoDialog.value = false;
              showInfoDialog.value = false;
            })
                  }, {
                    default: _withCtx(() => [...(_cache[49] || (_cache[49] = [
                      _createTextVNode(" Don't show again ", -1)
                    ]))]),
                    _: 1
                  }, 8, ["color"])
                ]),
                _: 1
              })
            ])
          ]),
          _: 1
        }, 8, ["modelValue", "style"]),
        _createVNode(_component_v_dialog, {
          class: "error-dialog",
          style: _normalizeStyle(cssVars.value),
          modelValue: showWebGL2Dialog.value,
          "onUpdate:modelValue": _cache[29] || (_cache[29] = ($event: any) => ((showWebGL2Dialog).value = $event)),
          persistent: ""
        }, {
          default: _withCtx(() => [
            _createVNode(_component_v_card, null, {
              default: _withCtx(() => [...(_cache[53] || (_cache[53] = [
                _createElementVNode("div", { class: "error-message" }, [
                  _createElementVNode("p", null, [
                    _createElementVNode("strong", null, "This app requires WebGL 2")
                  ]),
                  _createElementVNode("p", { class: "mt-2" }, " Check your browser's settings and enable WebGL 2 (\"graphics acceleration\" on some browsers). "),
                  _createElementVNode("p", { class: "mt-2" }, [
                    _createTextVNode(" You can check whether your browser supports WebGL 2 and get assistance "),
                    _createElementVNode("a", {
                      href: "https://get.webgl.org/webgl2/",
                      target: "_blank",
                      rel: "noopener noreferrer"
                    }, "here"),
                    _createTextVNode(". ")
                  ])
                ], -1)
              ]))]),
              _: 1
            })
          ]),
          _: 1
        }, 8, ["style", "modelValue"]),
        _createVNode(_component_v_dialog, {
          id: "video-container",
          modelValue: showVideoSheet.value,
          "onUpdate:modelValue": _cache[32] || (_cache[32] = ($event: any) => ((showVideoSheet).value = $event)),
          transition: "slide-y-transition",
          fullscreen: ""
        }, {
          default: _withCtx(() => [
            _createElementVNode("div", _hoisted_22, [
              _createVNode(_component_font_awesome_icon, {
                id: "video-close-icon",
                class: "close-icon",
                icon: "times",
                size: "lg",
                onClick: _cache[30] || (_cache[30] = ($event: any) => (showVideoSheet.value = false)),
                onKeyup: _cache[31] || (_cache[31] = _withKeys(($event: any) => (showVideoSheet.value = false), ["enter"])),
                tabindex: "0"
              }),
              _cache[54] || (_cache[54] = _createElementVNode("video", {
                controls: "",
                id: "info-video"
              }, [
                _createElementVNode("source", {
                  src: "",
                  type: "video/mp4"
                })
              ], -1))
            ])
          ]),
          _: 1
        }, 8, ["modelValue"]),
        _createVNode(_component_v_dialog, {
          style: _normalizeStyle(cssVars.value),
          class: _normalizeClass(['info-sheet', `info-sheet-${infoSheetLocation.value}`]),
          id: "text-info-sheet",
          "hide-overlay": "",
          persistent: "",
          "no-click-animation": "",
          absolute: "",
          scrim: false,
          location: "end",
          modelValue: showTextSheet.value,
          "onUpdate:modelValue": _cache[37] || (_cache[37] = ($event: any) => ((showTextSheet).value = $event)),
          transition: infoSheetTransition.value
        }, {
          default: _withCtx(() => [
            _createVNode(_component_v_card, { height: "100%" }, {
              default: _withCtx(() => [
                _createVNode(_component_v_tabs, {
                  modelValue: tab.value,
                  "onUpdate:modelValue": _cache[33] || (_cache[33] = ($event: any) => ((tab).value = $event)),
                  height: "32px",
                  color: textColor.value,
                  "slider-color": textColor.value,
                  id: "tabs",
                  dense: "",
                  "align-tabs": "end"
                }, {
                  default: _withCtx(() => [
                    _createVNode(_component_v_tab, {
                      class: "info-tabs",
                      tabindex: "0"
                    }, {
                      default: _withCtx(() => [...(_cache[55] || (_cache[55] = [
                        _createElementVNode("h3", null, "User Guide", -1)
                      ]))]),
                      _: 1
                    })
                  ]),
                  _: 1
                }, 8, ["modelValue", "color", "slider-color"]),
                _createVNode(_component_font_awesome_icon, {
                  id: "close-text-icon",
                  class: "control-icon",
                  icon: "times",
                  size: "lg",
                  onClick: _cache[34] || (_cache[34] = ($event: any) => (showTextSheet.value = false)),
                  onKeyup: _cache[35] || (_cache[35] = _withKeys(($event: any) => (showTextSheet.value = false), ["enter"])),
                  tabindex: "0"
                }),
                _createVNode(_component_v_window, {
                  modelValue: tab.value,
                  "onUpdate:modelValue": _cache[36] || (_cache[36] = ($event: any) => ((tab).value = $event)),
                  id: "tab-items",
                  class: "pb-2 no-bottom-border-radius"
                }, {
                  default: _withCtx(() => [
                    _createVNode(_component_v_window_item, null, {
                      default: _withCtx(() => [
                        _createVNode(_component_v_card, { class: "no-bottom-border-radius scrollable" }, {
                          default: _withCtx(() => [
                            _createVNode(_component_v_card_text, { class: "info-text no-bottom-border-radius" }, {
                              default: _withCtx(() => [
                                _createVNode(_component_v_container, { class: "pa-0" }, {
                                  default: _withCtx(() => [
                                    _cache[73] || (_cache[73] = _createElementVNode("h4", { class: "user-guide-header" }, "WWT Roman View Finder", -1)),
                                    _cache[74] || (_cache[74] = _createElementVNode("p", null, [
                                      _createTextVNode(" This "),
                                      _createElementVNode("a", {
                                        href: "https://www.worldwidetelescope.org/home",
                                        target: "_blank",
                                        rel: "noopener noreferrer"
                                      }, "WorldWide Telescope"),
                                      _createTextVNode(" (WWT) interactive provides a view of the Roman Space Telescope footprint on the sky. ")
                                    ], -1)),
                                    _cache[75] || (_cache[75] = _createElementVNode("h4", { class: "user-guide-header mt-5" }, "Technical Note", -1)),
                                    _cache[76] || (_cache[76] = _createElementVNode("p", null, [
                                      _createTextVNode(" This tool can match potential targets to Roman's field of view, but it is "),
                                      _createElementVNode("strong", null, "NOT meant to be used as a precision tool"),
                                      _createTextVNode(" for planning science observations. WWT's all-sky backgrounds may have small position offsets of 2-3\". ")
                                    ], -1)),
                                    _cache[77] || (_cache[77] = _createElementVNode("h4", { class: "user-guide-header mt-5" }, "Sky Navigation", -1)),
                                    _cache[78] || (_cache[78] = _createElementVNode("p", null, " To navigate the WWT view, use the following controls: ", -1)),
                                    _createVNode(_component_v_row, {
                                      align: "center",
                                      class: "mt-2 mx-3"
                                    }, {
                                      default: _withCtx(() => [
                                        _createVNode(_component_v_col, { cols: "4" }, {
                                          default: _withCtx(() => [
                                            _createVNode(_component_v_chip, {
                                              label: "",
                                              outlined: ""
                                            }, {
                                              default: _withCtx(() => [...(_cache[56] || (_cache[56] = [
                                                _createTextVNode(" Pan ", -1)
                                              ]))]),
                                              _: 1
                                            })
                                          ]),
                                          _: 1
                                        }),
                                        _createVNode(_component_v_col, {
                                          cols: "8",
                                          class: "pt-1"
                                        }, {
                                          default: _withCtx(() => [
                                            _createElementVNode("strong", null, _toDisplayString(_unref(touchscreen) ? "press + drag" : "click + drag"), 1),
                                            _createTextVNode(" " + _toDisplayString(_unref(touchscreen) ? "" : "or") + " ", 1),
                                            _createElementVNode("strong", null, _toDisplayString(_unref(touchscreen) ? "" : "W-A-S-D"), 1),
                                            _createTextVNode(" " + _toDisplayString(_unref(touchscreen) ? "" : "keys"), 1),
                                            _cache[57] || (_cache[57] = _createElementVNode("br", null, null, -1))
                                          ]),
                                          _: 1
                                        })
                                      ]),
                                      _: 1
                                    }),
                                    _createVNode(_component_v_row, {
                                      align: "center",
                                      class: "mx-3"
                                    }, {
                                      default: _withCtx(() => [
                                        _createVNode(_component_v_col, { cols: "4" }, {
                                          default: _withCtx(() => [
                                            _createVNode(_component_v_chip, {
                                              label: "",
                                              outlined: ""
                                            }, {
                                              default: _withCtx(() => [...(_cache[58] || (_cache[58] = [
                                                _createTextVNode(" Zoom ", -1)
                                              ]))]),
                                              _: 1
                                            })
                                          ]),
                                          _: 1
                                        }),
                                        _createVNode(_component_v_col, {
                                          cols: "8",
                                          class: "pt-1"
                                        }, {
                                          default: _withCtx(() => [
                                            _createElementVNode("strong", null, _toDisplayString(_unref(touchscreen) ? "pinch in and out" : "scroll in and out"), 1),
                                            _createTextVNode(" " + _toDisplayString(_unref(touchscreen) ? "" : "or") + " ", 1),
                                            _createElementVNode("strong", null, _toDisplayString(_unref(touchscreen) ? "" : "I-O"), 1),
                                            _createTextVNode(" " + _toDisplayString(_unref(touchscreen) ? "" : "keys"), 1),
                                            _cache[59] || (_cache[59] = _createElementVNode("br", null, null, -1))
                                          ]),
                                          _: 1
                                        })
                                      ]),
                                      _: 1
                                    }),
                                    _createVNode(_component_v_row, {
                                      align: "center",
                                      class: "mx-3"
                                    }, {
                                      default: _withCtx(() => [
                                        _createVNode(_component_v_col, { cols: "4" }, {
                                          default: _withCtx(() => [
                                            _createVNode(_component_v_chip, {
                                              label: "",
                                              outlined: ""
                                            }, {
                                              default: _withCtx(() => [...(_cache[60] || (_cache[60] = [
                                                _createTextVNode(" Rotate ", -1)
                                              ]))]),
                                              _: 1
                                            })
                                          ]),
                                          _: 1
                                        }),
                                        _createVNode(_component_v_col, {
                                          cols: "8",
                                          class: "pt-1"
                                        }, {
                                          default: _withCtx(() => [
                                            _createTextVNode(_toDisplayString(_unref(touchscreen) ? "" : "press") + " ", 1),
                                            _createElementVNode("strong", null, _toDisplayString(_unref(touchscreen) ? "pinch and twist" : "control + click + drag"), 1),
                                            _createTextVNode(" " + _toDisplayString(_unref(touchscreen) ? "" : "") + " ", 1),
                                            _createElementVNode("strong", null, _toDisplayString(_unref(touchscreen) ? "" : ""), 1),
                                            _createTextVNode(" " + _toDisplayString(_unref(touchscreen) ? "" : "") + " (Keyboard option coming soon)", 1),
                                            _cache[61] || (_cache[61] = _createElementVNode("br", null, null, -1))
                                          ]),
                                          _: 1
                                        })
                                      ]),
                                      _: 1
                                    }),
                                    _createVNode(_component_v_row, null, {
                                      default: _withCtx(() => [
                                        _createVNode(_component_v_col, { cols: "12" }, {
                                          default: _withCtx(() => [
                                            _cache[70] || (_cache[70] = _createElementVNode("h4", { class: "user-guide-header" }, "Controls", -1)),
                                            _createElementVNode("ul", _hoisted_23, [
                                              _cache[64] || (_cache[64] = _createElementVNode("li", null, [
                                                _createElementVNode("strong", null, "Select Background"),
                                                _createTextVNode(": Choose from DSS, 2MASS, SDSS, or unWISE. ")
                                              ], -1)),
                                              _cache[65] || (_cache[65] = _createElementVNode("li", null, [
                                                _createElementVNode("strong", null, "Footprint Color"),
                                                _createTextVNode(": Adjust as needed to contrast against the background. ")
                                              ], -1)),
                                              _cache[66] || (_cache[66] = _createElementVNode("li", null, [
                                                _createElementVNode("strong", null, "Crosshairs"),
                                                _createTextVNode(": Show location reported in RA/Dec display and adjust color as needed. ")
                                              ], -1)),
                                              _cache[67] || (_cache[67] = _createElementVNode("li", null, [
                                                _createElementVNode("strong", null, "Fill"),
                                                _createTextVNode(": Fill in footprint squares and adjust opacity. ")
                                              ], -1)),
                                              _cache[68] || (_cache[68] = _createElementVNode("li", null, [
                                                _createElementVNode("strong", null, "Decimal coordinates"),
                                                _createTextVNode(": Display RA/Dec as decimals. ")
                                              ], -1)),
                                              _cache[69] || (_cache[69] = _createElementVNode("li", null, [
                                                _createElementVNode("strong", null, "Galactic mode"),
                                                _createTextVNode(": rotate WWT view to follow galactic plane. ")
                                              ], -1)),
                                              _createElementVNode("li", null, [
                                                _createVNode(_component_font_awesome_icon, {
                                                  class: "bullet-icon",
                                                  icon: "info",
                                                  size: "lg"
                                                }),
                                                _cache[62] || (_cache[62] = _createTextVNode(" View this documentation ", -1))
                                              ]),
                                              _createElementVNode("li", null, [
                                                _createVNode(_component_font_awesome_icon, {
                                                  class: "bullet-icon",
                                                  icon: "share-nodes",
                                                  size: "lg"
                                                }),
                                                _cache[63] || (_cache[63] = _createTextVNode(" Get link to share current view. Url will be copied to clipboard and can be pasted in browser. ", -1))
                                              ])
                                            ])
                                          ]),
                                          _: 1
                                        })
                                      ]),
                                      _: 1
                                    }),
                                    _createVNode(_component_v_row, null, {
                                      default: _withCtx(() => [
                                        _createVNode(_component_v_col, { cols: "12" }, {
                                          default: _withCtx(() => [...(_cache[71] || (_cache[71] = [
                                            _createElementVNode("h4", { class: "user-guide-header" }, "Go to", -1),
                                            _createElementVNode("ul", { class: "text-list mx-5" }, [
                                              _createElementVNode("li", null, [
                                                _createElementVNode("strong", null, "Object Name"),
                                                _createTextVNode(": Use SIMBAD search to resolve object names. ")
                                              ]),
                                              _createElementVNode("p", { style: {"color":"var(--border-color)"} }, [
                                                _createElementVNode("strong", null, "or")
                                              ]),
                                              _createElementVNode("li", null, [
                                                _createElementVNode("strong", null, "RA"),
                                                _createTextVNode(": Right Ascension entered in sexagesimal format (00h00m00s, 00:00:00, or 00 00 00) will be interpreted as hours. Decimal format will be interpreted as degrees. ")
                                              ]),
                                              _createElementVNode("li", null, [
                                                _createElementVNode("strong", null, "Dec"),
                                                _createTextVNode(": Declination can be entered in sexagesimal format (00°00'00\", 00:00:00, or 00 00 00) or in decimal format. ")
                                              ])
                                            ], -1)
                                          ]))]),
                                          _: 1
                                        })
                                      ]),
                                      _: 1
                                    }),
                                    _createVNode(_component_v_row, null, {
                                      default: _withCtx(() => [
                                        _createVNode(_component_v_col, { cols: "12" }, {
                                          default: _withCtx(() => [
                                            _cache[72] || (_cache[72] = _createElementVNode("div", { class: "credits" }, [
                                              _createElementVNode("h4", { class: "user-guide-header mt-3" }, "Credits"),
                                              _createElementVNode("h5", null, [
                                                _createElementVNode("a", {
                                                  href: "https://www.cosmicds.cfa.harvard.edu/",
                                                  target: "_blank",
                                                  rel: "noopener noreferrer"
                                                }, "CosmicDS")
                                              ]),
                                              _createElementVNode("p", null, "Jon Carifio"),
                                              _createElementVNode("p", null, "John Lewis"),
                                              _createElementVNode("p", null, "Pat Udomprasert"),
                                              _createElementVNode("p", null, "Alyssa Goodman"),
                                              _createElementVNode("h5", null, [
                                                _createElementVNode("a", {
                                                  href: "https://www.worldwidetelescope.org/home",
                                                  target: "_blank",
                                                  rel: "noopener noreferrer"
                                                }, "WorldWide Telescope")
                                              ]),
                                              _createElementVNode("p", null, "Jon Carifio"),
                                              _createElementVNode("p", null, "Peter Williams"),
                                              _createElementVNode("p", null, "David Weigel")
                                            ], -1)),
                                            _createVNode(_component_v_spacer, { class: "end-spacer" })
                                          ]),
                                          _: 1
                                        })
                                      ]),
                                      _: 1
                                    }),
                                    _createVNode(_component_v_row, null, {
                                      default: _withCtx(() => [
                                        _createVNode(_component_v_col, null, {
                                          default: _withCtx(() => [
                                            _createVNode(_component_funding_acknowledgement)
                                          ]),
                                          _: 1
                                        })
                                      ]),
                                      _: 1
                                    })
                                  ]),
                                  _: 1
                                })
                              ]),
                              _: 1
                            })
                          ]),
                          _: 1
                        })
                      ]),
                      _: 1
                    })
                  ]),
                  _: 1
                }, 8, ["modelValue"])
              ]),
              _: 1
            })
          ]),
          _: 1
        }, 8, ["style", "class", "modelValue", "transition"])
      ])
    ]),
    _: 1
  }, 8, ["style"]))
}
}

})